import type { PatientGroupSchema as PatientGroup } from '@store/generated/data-migration';

// ---------------------------------------------------------------------------
// Selection state
// ---------------------------------------------------------------------------

/** Composite key: `${patientId}::${moduleKey}::${itemIndex}` */
export type SelectionKey = string;

export function makeSelectionKey(
  patientId: string,
  moduleKey: string,
  itemIndex: number,
): SelectionKey {
  return `${patientId}::${moduleKey}::${itemIndex}`;
}

export function parseSelectionKey(key: SelectionKey): {
  patientId: string;
  moduleKey: string;
  itemIndex: number;
} {
  const [patientId, moduleKey, itemIndexStr] = key.split('::');
  return { patientId, moduleKey, itemIndex: Number(itemIndexStr) };
}

// ---------------------------------------------------------------------------
// Normalized module data
// ---------------------------------------------------------------------------

export interface NormalizedModuleItem {
  /** Raw item data (varies by module type) */
  data: Record<string, unknown>;
  patientId: string;
  patientName: string;
  isPrimary: boolean;
  /** The patient_module_id (key within patient_module_data) */
  moduleKey: string;
  /** The module's name from the API (e.g. "medications", "diagnoses") */
  moduleName: string;
  /** Key within current_data (e.g. "diagnoses", "medications") */
  dataKey: string;
  itemIndex: number;
  selectionKey: SelectionKey;
  /** Human-readable label computed from the item data + schema */
  displayLabel: string;
}

export interface NormalizedModule {
  /** Canonical display name (e.g. "Diagnoses") */
  name: string;
  /** All items from all patients, flattened */
  items: NormalizedModuleItem[];
}

/** Keyed by canonical module name (lowercased) */
export type NormalizedModuleMap = Record<string, NormalizedModule>;

// ---------------------------------------------------------------------------
// Duplicate detection
// ---------------------------------------------------------------------------

/** Map from canonical identity string → array of selectionKeys that share it */
export type DuplicateMap = Map<string, SelectionKey[]>;

// ---------------------------------------------------------------------------
// Router state passed from dedup page → customization page
// ---------------------------------------------------------------------------

export interface MergeCustomizationLocationState {
  group: PatientGroup;
  /** Now resolved on the customization page; optional from navigation */
  primaryPatientId?: string;
  organizationId: string;
}

/** State passed back to dedup page on back-navigation */
export interface DeduplicationRestoredState {
  deduplicationResult: {
    duplicate_groups: PatientGroup[];
    total_patients: number;
    total_duplicates_found: number;
  };
}

// ---------------------------------------------------------------------------
// Backend API contract (proposed — not yet supported)
// ---------------------------------------------------------------------------

export interface ModuleItemSelection {
  module_name: string;
  source_patient_id: string;
  item_index: number;
}

